import type { Notification } from '@cyphex/types/notifications';
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api/client';
import { query } from '@/lib/api/query';
import { getRealtimeClient } from '@/lib/realtime';
import type { DeliveryVerdict, NotificationApi } from './notificationsAdapter';
import { toNotificationItem } from './notificationWire';
import { toMutes, toNotificationSettings, toRules } from './settingsWire';

const DEFAULT_LIMIT = 50;

/**
 * Live Activity feed: REST for history, `user:<self>` on the gateway for
 * everything new. `notification.push` is a DURABLE event, so a client that was
 * offline gets its missed rows replayed on resume — the list never silently
 * skips a mention.
 */
export const notificationsHttp: NotificationApi = {
  async list({ before, limit = DEFAULT_LIMIT }) {
    // The feed pages on `cursor`, not `after`: sending the wrong name made the
    // server silently return page 1 forever, so "load more" repeated itself.
    const payload = await apiGet<{ notifications: Notification[]; nextCursor: string | null }>(
      `/notifications${query({ cursor: before, limit })}`,
    );
    return {
      items: (payload.notifications ?? []).map(toNotificationItem),
      nextCursor: payload.nextCursor,
    };
  },

  async markRead(id) {
    await apiPost('/notifications/read', { notificationIds: [id] });
  },

  async markAllSeen() {
    await apiPost('/notifications/seen', {});
  },

  async markAllRead() {
    await apiPost('/notifications/read', { all: true });
  },

  async dismiss(id) {
    await apiDelete(`/notifications/${id}`);
  },

  async getSettings() {
    return toNotificationSettings(await apiGet<Record<string, unknown>>('/notifications/settings'));
  },

  async saveSettings(patch) {
    await apiPut('/notifications/settings', patch);
  },

  async setMute(input) {
    const payload = await apiPost<{ mutes: unknown }>('/notifications/mutes', input);
    return toMutes(payload.mutes);
  },

  async saveRule(input) {
    const payload = await apiPost<{ rule: unknown }>('/notifications/rules', {
      ...input,
      keywords: [...input.keywords],
    });
    const [rule] = toRules([payload.rule]);
    if (rule === undefined) throw new Error('rule was not persisted');
    return rule;
  },

  async deleteRule(id) {
    await apiDelete(`/notifications/rules/${id}`);
  },

  subscribe(onNotification) {
    const realtime = getRealtimeClient();
    realtime.connect();
    /* `user:<self>` is auto-subscribed by the gateway on handshake. */
    return realtime.onEvent(({ event }) => {
      if (event.type !== 'notification.push') return;
      /* `delivery` is absent on events emitted before 055 — treat that as
         "allow", never as "suppress", or an old server silences the client. */
      const delivery = (event as { delivery?: DeliveryVerdict }).delivery ?? null;
      onNotification(toNotificationItem(event.notification), delivery);
    });
  },

  subscribeDismissals(onDismiss) {
    const realtime = getRealtimeClient();
    realtime.connect();
    return realtime.onEvent(({ event }) => {
      if (event.type !== 'notification.dismissed') return;
      onDismiss(event.notificationIds, event.groupKey);
    });
  },
};
